package com.kanati.admin.state

import com.kanati.shared.admin.CurrentOperatorContextResponse
import com.kanati.shared.district.District
import com.kanati.shared.school.School

data class SelectedDistrictContext(
    val id: String,
    val name: String,
    val detail: String? = null,
    val city: String? = null,
    val county: String? = null,
    val state: String? = null,
    val timezone: String? = null,
    val defaultTaxRateBps: Int? = null,
)

data class SelectedSchoolContext(
    val id: String,
    val districtId: String,
    val name: String,
    val shortName: String? = null,
    val active: Boolean,
)

data class DistrictContextState(
    val districts: List<District> = emptyList(),
    val schoolsByDistrictId: Map<String, List<School>> = emptyMap(),
    val selectedDistrictId: String? = null,
    val selectedSchoolIdByDistrictId: Map<String, String> = emptyMap(),
    val isLoading: Boolean = false,
    val error: String? = null,
) {
    val selectedDistrict: District?
        get() = districts.firstOrNull { it.id == selectedDistrictId }

    val selectedDistrictName: String?
        get() = currentDistrict?.name

    val currentDistrict: SelectedDistrictContext?
        get() = selectedDistrict?.toSelectedDistrictContext()

    val availableSchools: List<School>
        get() = selectedDistrictId?.let { schoolsByDistrictId[it].orEmpty() }.orEmpty()

    val selectedSchoolId: String?
        get() = selectedDistrictId?.let(selectedSchoolIdByDistrictId::get)

    val selectedSchool: School?
        get() = availableSchools.firstOrNull { it.id == selectedSchoolId }

    val currentSchool: SelectedSchoolContext?
        get() = selectedSchool?.toSelectedSchoolContext()
}

fun CurrentOperatorContextResponse.authorizedDistricts(): List<District> =
    (districtAccess.map { it.district } + schoolAccess.map { it.district })
        .distinctBy { it.id }
        .sortedBy { it.name.lowercase() }

fun CurrentOperatorContextResponse.accessibleSchoolsByDistrict(): Map<String, List<School>> {
    val schools = buildMap<String, MutableList<School>> {
        districtAccess.forEach { access ->
            val bucket = getOrPut(access.district.id) { mutableListOf() }
            access.schools.forEach { school ->
                if (bucket.none { it.id == school.id }) {
                    bucket += school
                }
            }
        }
        schoolAccess.forEach { access ->
            val bucket = getOrPut(access.district.id) { mutableListOf() }
            if (bucket.none { it.id == access.school.id }) {
                bucket += access.school
            }
        }
        operableSchools.forEach { access ->
            val bucket = getOrPut(access.district.id) { mutableListOf() }
            if (bucket.none { it.id == access.school.id }) {
                bucket += access.school
            }
        }
    }

    return schools.mapValues { (_, list) -> list.sortedBy { it.name.lowercase() } }
}

fun CurrentOperatorContextResponse.accessibleSchoolsForDistrict(
    districtId: String,
    knownSchools: List<School> = emptyList(),
): List<School> {
    val catalog = (knownSchools +
        districtAccess
            .filter { it.district.id == districtId }
            .flatMap { it.schools } +
        schoolAccess
            .filter { it.district.id == districtId }
            .map { it.school } +
        operableSchools
            .filter { it.district.id == districtId }
            .map { it.school })
        .distinctBy { it.id }

    if (catalog.isEmpty()) {
        return emptyList()
    }

    val accessibleSchoolIds = buildSet {
        districtAccess
            .filter { it.district.id == districtId }
            .forEach { access ->
                if (access.districtWide && (access.adminAccess || access.posAccess)) {
                    knownSchools.forEach { school -> add(school.id) }
                }
                access.schools.forEach { school -> add(school.id) }
            }
        schoolAccess
            .filter { it.district.id == districtId }
            .forEach { access -> add(access.school.id) }
        operableSchools
            .filter { it.district.id == districtId }
            .forEach { access -> add(access.school.id) }
    }

    return catalog
        .filter { it.id in accessibleSchoolIds }
        .sortedBy { it.name.lowercase() }
}

fun CurrentOperatorContextResponse.hasDistrictAdminAccess(districtId: String): Boolean =
    districtAccess.any { it.district.id == districtId && it.adminAccess }

fun CurrentOperatorContextResponse.hasSchoolAdminAccess(
    districtId: String,
    schoolId: String,
): Boolean =
    districtAccess.any { access ->
        access.district.id == districtId &&
            access.adminAccess &&
            (
                access.districtWide ||
                    access.schools.any { school -> school.id == schoolId }
                )
    } || schoolAccess.any { access ->
        access.district.id == districtId &&
            access.school.id == schoolId &&
            access.adminAccess
    }

fun District.toSelectedDistrictContext(): SelectedDistrictContext =
    SelectedDistrictContext(
        id = id,
        name = name,
        detail = "$city, ${state.name}",
        city = city,
        county = county,
        state = state.name,
        timezone = timezone,
        defaultTaxRateBps = defaultTaxRateBps,
    )

fun School.toSelectedSchoolContext(): SelectedSchoolContext =
    SelectedSchoolContext(
        id = id,
        districtId = districtId,
        name = name,
        shortName = shortName,
        active = active,
    )
